import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import Styles from './CustomiseLayout.module.scss'
import { useField } from 'formik'
import { Button } from 'blaze-ui/Button'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { PaletteField } from '../../Fields/PaletteField/PaletteField'
import { TextStylesField } from '../../Fields/TextStylesField/TextStylesField'
import { CustomiseLayoutProps } from './Types'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { useEffect, useMemo, useState } from 'react'
import { Ref } from 'components/DesignContentPreview/Types'
import { FontPairing } from 'constants/fontPairings'
import { useExtractColors } from 'components/VisualStudioPlayground/VisualEditor/hooks/useExtractColors'
import { useExtractTextStyles } from 'components/VisualStudioPlayground/VisualEditor/hooks/useExtractTextStyles'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

const CustomiseLayout = observer(
  multiStep<CustomiseLayoutProps>((props) => {
    const { visualElements } = useRootStore()
    const { schema, submit, initialValues, getSchemaField } = props
    const { content, source_visual_element_id } = initialValues
    const { meta } = schema.describe()

    const [editorRef, setEditorRef] = useState<Ref | null>(null)
    const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null)

    const [{ value: textStyles }, ,] = useField('text_styles')
    const [{ value: palette }, ,] = useField('palette')
    const [, , { setValue: setContent }] = useField('content')

    const extractColors = useExtractColors()
    const extractTextStyles = useExtractTextStyles()

    const paletteField = getSchemaField('palette')
    const textStylesField = getSchemaField('text_styles')

    const template = source_visual_element_id ? visualElements.getElement(source_visual_element_id) : undefined
    const templatePalette = useMemo(
      () => (template ? extractColors(template.content['snapshot']) : undefined),
      [template], // eslint-disable-line react-hooks/exhaustive-deps
    )
    const templateTextStyles = useMemo(
      () => (template ? extractTextStyles(template.content['snapshot']) : undefined),
      [template], // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
      function applyTextStyles() {
        if (!editorRef) return
        editorRef.applyTextStyles(textStyles).then((snapshot) => {
          if (snapshot) {
            setContent(snapshot)
          }
        })
      },
      [textStyles], // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
      function applyPalette() {
        if (!editorRef) return
        editorRef.applyPalette(palette).then((snapshot) => {
          if (snapshot) {
            setContent(snapshot)
          }
        })
      },
      [palette], // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
      function zoomOnCurrentFrame() {
        if (editorRef) {
          setTimeout(() => {
            editorRef.setCameraToCurrentFrame({ animate: false, isMobile: true })
          }, 10)
        }
      },
      [editorRef], // eslint-disable-line react-hooks/exhaustive-deps
    )

    return (
      <div className={Styles.wrapper}>
        <MobileWizard.Content>
          <MobileWizard.Section>
            <MobileWizard.Title>{meta.title}</MobileWizard.Title>
          </MobileWizard.Section>

          <div className={Styles.preview} ref={setPreviewRef}>
            {previewRef && (
              <div style={{ height: previewRef.clientHeight, width: '100%' }}>
                <DesignContentPreview content={content} ref={setEditorRef} />
              </div>
            )}
          </div>
        </MobileWizard.Content>

        <div className={Styles.footer}>
          <div className={Styles.buttons}>
            {paletteField && <PaletteField editorRef={editorRef ?? undefined} templatePalette={templatePalette} />}
            {textStylesField && (
              <TextStylesField
                editorRef={editorRef ?? undefined}
                templateTextStyles={templateTextStyles as FontPairing}
              />
            )}
          </div>
          <Button size="lg" fullWidth onPress={() => submit()}>
            Open Design
          </Button>
        </div>
      </div>
    )
  }),
)

export { CustomiseLayout }
